import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useKeyboardShortcuts } from '@/context/KeyboardShortcutsContext';
import { useSidebar } from '@/components/ui/sidebar';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useAccountSettings } from '@/context/AccountSettingsContext';
import { useBusinessContext } from '@/context/BusinessContext';
import { useBusinessSettings, BusinessSettingsSection } from '@/context/BusinessSettingsContext';
import {
  LayoutDashboard,
  Users,
  Star,
  Settings,
  Settings2,
  PanelLeft,
  CreditCard,
  Gift,
  BarChart3,
  Clock,
  Building2,
  Briefcase,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Bot,
  Store,
} from 'lucide-react';

// Dashboard routes in order for Ctrl+Left/Right navigation
const DASHBOARDS = ['/dashboard', '/employee-dashboard', '/portfolio'] as const;

// Tabs for each dashboard (in order for Ctrl+Up/Down navigation)
const BUSINESS_TABS = [
  'reviews', 'insights', 'forms', 'google', 'feedback-intelligence',
  'customers', 'loyalty', 'loyalty-statistics', 'coupons', 'bulk-mail', 'email-templates',
  'social-accounts', 'social-posts', 'social-inbox', 'social-analytics',
  'signage'
];

const EMPLOYEE_TABS = [
  'insights', 'time-tracking', 'shift-management', 'payroll', 'employee-list', 'communications'
];

const FRANCHISE_TABS = [
  'overview', 'venues', 'schedule', 'analytics', 'payroll', 'alerts', 'custom-solutions'
];

// Map current tab to appropriate business settings section
const TAB_TO_SETTINGS_SECTION: Record<string, BusinessSettingsSection> = {
  // Employee dashboard tabs
  'time-tracking': 'time-tracking',
  'shift-management': 'employee-shifts',
  'payroll': 'time-tracking',
  'employee-list': 'employee-shifts',
  'communications': 'business-info',
  // Business dashboard tabs
  'reviews': 'business-info',
  'insights': 'business-info',
  'forms': 'business-info',
  'google': 'integrations',
  'feedback-intelligence': 'business-info',
  'customers': 'business-info',
  'loyalty': 'business-info',
  'loyalty-statistics': 'business-info',
  'coupons': 'business-info',
  'bulk-mail': 'integrations',
  'email-templates': 'integrations',
  'social-accounts': 'integrations',
  'social-posts': 'integrations',
  'social-inbox': 'integrations',
  'social-analytics': 'integrations',
  'signage': 'integrations',
  // Portfolio/franchise tabs
  'overview': 'business-info',
  'venues': 'business-info',
  'schedule': 'employee-shifts',
  'analytics': 'business-info',
  'alerts': 'business-info',
  'custom-solutions': 'business-info',
};

export function DefaultCommands() {
  const { registerCommand } = useKeyboardShortcuts();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar, aiModeActive, setAiModeActive, state, displayMode, setDisplayMode } = useSidebar();
  const { businesses, selectedBusinessId, switchBusiness } = useBusinessContext();
  const {
    businessDashboardTab,
    setBusinessDashboardTab,
    employeeDashboardTab,
    setEmployeeDashboardTab,
    franchiseDashboardTab,
    setFranchiseDashboardTab,
  } = useDashboardTab();
  const { openAccountSettings } = useAccountSettings();
  const { openBusinessSettings } = useBusinessSettings();

  // Determine current dashboard index
  const getCurrentDashboardIndex = useCallback(() => {
    const path = location.pathname;
    if (path.startsWith('/dashboard')) return 0;
    if (path.startsWith('/employee-dashboard')) return 1;
    if (path.startsWith('/portfolio')) return 2;
    return -1;
  }, [location.pathname]);

  // Get current tab info based on dashboard
  const getCurrentTabInfo = useCallback(() => {
    const dashboardIndex = getCurrentDashboardIndex();
    switch (dashboardIndex) {
      case 0:
        return { tabs: BUSINESS_TABS, currentTab: businessDashboardTab, setTab: setBusinessDashboardTab };
      case 1:
        return { tabs: EMPLOYEE_TABS, currentTab: employeeDashboardTab, setTab: setEmployeeDashboardTab };
      case 2:
        return { tabs: FRANCHISE_TABS, currentTab: franchiseDashboardTab, setTab: setFranchiseDashboardTab };
      default:
        return null;
    }
  }, [getCurrentDashboardIndex, businessDashboardTab, employeeDashboardTab, franchiseDashboardTab, setBusinessDashboardTab, setEmployeeDashboardTab, setFranchiseDashboardTab]);

  // Navigate to next/previous dashboard
  const navigateDashboard = useCallback((direction: 'next' | 'prev') => {
    const currentIndex = getCurrentDashboardIndex();
    if (currentIndex === -1) {
      // Not on a dashboard, go to business dashboard
      navigate('/dashboard');
      return;
    }

    let newIndex: number;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % DASHBOARDS.length;
    } else {
      newIndex = (currentIndex - 1 + DASHBOARDS.length) % DASHBOARDS.length;
    }
    navigate(DASHBOARDS[newIndex]);
  }, [getCurrentDashboardIndex, navigate]);

  // Toggle AI chat panel
  const toggleAI = useCallback(() => {
    if (!aiModeActive) {
      // Opening AI - expand sidebar if collapsed
      if (state === 'collapsed' || displayMode === 'always-collapsed') {
        setDisplayMode('always-expanded');
      }
      setAiModeActive(true);
    } else {
      setAiModeActive(false);
    }
  }, [aiModeActive, state, displayMode, setDisplayMode, setAiModeActive]);

  // Switch to next/previous business
  const cycleBusiness = useCallback((direction: 'next' | 'prev') => {
    if (businesses.length <= 1) return;

    const currentIndex = businesses.findIndex(b => b.id === selectedBusinessId);
    let newIndex: number;

    if (currentIndex === -1) {
      newIndex = 0;
    } else if (direction === 'next') {
      newIndex = (currentIndex + 1) % businesses.length;
    } else {
      newIndex = (currentIndex - 1 + businesses.length) % businesses.length;
    }

    switchBusiness(businesses[newIndex].id);
  }, [businesses, selectedBusinessId, switchBusiness]);

  // Navigate to next/previous tab
  const navigateTab = useCallback((direction: 'next' | 'prev') => {
    const tabInfo = getCurrentTabInfo();
    if (!tabInfo) return;

    const { tabs, currentTab, setTab } = tabInfo;
    const currentIndex = tabs.indexOf(currentTab);

    let newIndex: number;
    if (currentIndex === -1) {
      newIndex = 0;
    } else if (direction === 'next') {
      newIndex = (currentIndex + 1) % tabs.length;
    } else {
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    }
    setTab(tabs[newIndex]);
  }, [getCurrentTabInfo]);

  // Get the current tab name for business settings section mapping
  const getCurrentTab = useCallback(() => {
    const tabInfo = getCurrentTabInfo();
    return tabInfo?.currentTab || null;
  }, [getCurrentTabInfo]);

  // Open business settings with context-aware section
  const handleOpenBusinessSettings = useCallback(() => {
    const currentTab = getCurrentTab();
    const section = currentTab ? TAB_TO_SETTINGS_SECTION[currentTab] : undefined;
    openBusinessSettings(section || 'business-info');
  }, [getCurrentTab, openBusinessSettings]);

  useEffect(() => {
    const unregisters: (() => void)[] = [];

    // Arrow key navigation
    unregisters.push(
      registerCommand({
        id: 'nav-dashboard-next',
        keys: { key: 'ArrowRight', modifiers: ['ctrl'] },
        label: 'Next Dashboard',
        description: 'Cycle to next dashboard',
        action: () => navigateDashboard('next'),
        category: 'navigation',
        icon: ArrowRight,
        keywords: ['arrow', 'switch', 'cycle'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-dashboard-prev',
        keys: { key: 'ArrowLeft', modifiers: ['ctrl'] },
        label: 'Previous Dashboard',
        description: 'Cycle to previous dashboard',
        action: () => navigateDashboard('prev'),
        category: 'navigation',
        icon: ArrowLeft,
        keywords: ['arrow', 'switch', 'cycle'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-tab-next',
        keys: { key: 'ArrowDown', modifiers: ['ctrl'] },
        label: 'Next Tab',
        description: 'Cycle to next tab',
        action: () => navigateTab('next'),
        category: 'navigation',
        icon: ArrowDown,
        keywords: ['arrow', 'switch', 'cycle'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-tab-prev',
        keys: { key: 'ArrowUp', modifiers: ['ctrl'] },
        label: 'Previous Tab',
        description: 'Cycle to previous tab',
        action: () => navigateTab('prev'),
        category: 'navigation',
        icon: ArrowUp,
        keywords: ['arrow', 'switch', 'cycle'],
      })
    );

    // Main navigation commands (shown in palette)
    unregisters.push(
      registerCommand({
        id: 'nav-business-dashboard',
        label: 'Business Dashboard',
        description: 'Main business dashboard',
        action: () => navigate('/dashboard'),
        category: 'navigation',
        icon: LayoutDashboard,
        keywords: ['home', 'main', 'business', 'overview'],
        context: {
          dashboards: ['employee', 'portfolio'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-employee-dashboard',
        label: 'Employee Dashboard',
        description: 'Employee management',
        action: () => navigate('/employee-dashboard'),
        category: 'navigation',
        icon: Users,
        keywords: ['staff', 'team', 'workers', 'employees'],
        context: {
          dashboards: ['business', 'portfolio'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-portfolio',
        label: 'Portfolio',
        description: 'Franchise portfolio',
        action: () => navigate('/portfolio'),
        category: 'navigation',
        icon: Building2,
        keywords: ['franchise', 'multi', 'businesses', 'overview'],
        context: {
          dashboards: ['business', 'employee'],
        },
      })
    );

    // Business dashboard tabs
    unregisters.push(
      registerCommand({
        id: 'nav-reviews',
        label: 'Reviews',
        description: 'Customer reviews',
        action: () => {
          navigate('/dashboard');
          setBusinessDashboardTab('reviews');
        },
        category: 'navigation',
        icon: Star,
        keywords: ['feedback', 'ratings', 'google', 'stars'],
        context: {
          dashboards: ['business'],
          tabs: ['reviews', 'insights', 'forms', 'google', 'feedback-intelligence'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-insights',
        label: 'Insights',
        description: 'Review analytics',
        action: () => {
          navigate('/dashboard');
          setBusinessDashboardTab('insights');
        },
        category: 'navigation',
        icon: BarChart3,
        keywords: ['analytics', 'statistics', 'charts'],
        context: {
          dashboards: ['business'],
          tabs: ['reviews', 'insights', 'forms', 'google', 'feedback-intelligence'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-loyalty',
        label: 'Loyalty',
        description: 'Loyalty programs',
        action: () => {
          navigate('/dashboard');
          setBusinessDashboardTab('loyalty');
        },
        category: 'navigation',
        icon: Gift,
        keywords: ['rewards', 'passes', 'wallet', 'apple', 'google'],
        context: {
          dashboards: ['business'],
          tabs: ['customers', 'loyalty', 'loyalty-statistics', 'coupons'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-customers',
        label: 'Customers',
        description: 'Customer management',
        action: () => {
          navigate('/dashboard');
          setBusinessDashboardTab('customers');
        },
        category: 'navigation',
        icon: Briefcase,
        keywords: ['crm', 'contacts', 'clients'],
        context: {
          dashboards: ['business'],
          tabs: ['customers', 'loyalty', 'loyalty-statistics', 'coupons'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-coupons',
        label: 'Coupons',
        description: 'Coupon management',
        action: () => {
          navigate('/dashboard');
          setBusinessDashboardTab('coupons');
        },
        category: 'navigation',
        icon: CreditCard,
        keywords: ['offers', 'discounts', 'tokens'],
        context: {
          dashboards: ['business'],
          tabs: ['customers', 'loyalty', 'loyalty-statistics', 'coupons'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-time-tracking',
        label: 'Time Tracking',
        description: 'Employee hours',
        action: () => {
          navigate('/employee-dashboard');
          setEmployeeDashboardTab('time-tracking');
        },
        category: 'navigation',
        icon: Clock,
        keywords: ['hours', 'attendance', 'clock'],
        context: {
          dashboards: ['employee'],
          tabs: ['time-tracking', 'payroll', 'insights'],
        },
      })
    );

    unregisters.push(
      registerCommand({
        id: 'nav-shift-management',
        label: 'Shift Management',
        description: 'Schedule shifts',
        action: () => {
          navigate('/employee-dashboard');
          setEmployeeDashboardTab('shift-management');
        },
        category: 'navigation',
        icon: Clock,
        keywords: ['schedule', 'planning', 'roster', 'calendar'],
        context: {
          dashboards: ['employee'],
          tabs: ['shift-management', 'employee-list', 'insights'],
        },
      })
    );

    // Settings (opens sidebar panel, not a page)
    unregisters.push(
      registerCommand({
        id: 'open-settings',
        label: 'Account Settings',
        description: 'Personal account settings',
        keys: { key: ',', modifiers: ['ctrl'] },
        action: () => openAccountSettings(),
        category: 'settings',
        icon: Settings,
        keywords: ['preferences', 'configuration', 'options', 'profile', 'account', 'personal'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'open-business-settings',
        label: 'Business Settings',
        description: 'Business configuration',
        keys: { key: '.', modifiers: ['ctrl'] },
        action: handleOpenBusinessSettings,
        category: 'settings',
        icon: Settings2,
        keywords: ['business', 'settings', 'configuration', 'venue', 'hours', 'integrations'],
      })
    );

    // Action commands
    unregisters.push(
      registerCommand({
        id: 'toggle-sidebar',
        label: 'Toggle Sidebar',
        description: 'Show/hide sidebar',
        keys: { key: 'b', modifiers: ['ctrl'] },
        action: toggleSidebar,
        category: 'actions',
        icon: PanelLeft,
        keywords: ['menu', 'navigation', 'panel', 'collapse'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'toggle-ai-chat',
        label: 'Toggle AI Assistant',
        description: 'Open/close AI chat',
        keys: { key: ' ', modifiers: ['ctrl'] },
        action: toggleAI,
        category: 'actions',
        icon: Bot,
        keywords: ['ai', 'chat', 'assistant', 'bot', 'help'],
      })
    );

    // Business switching
    unregisters.push(
      registerCommand({
        id: 'switch-business-next',
        label: 'Next Business',
        description: 'Switch to next business',
        keys: { key: ']', modifiers: ['ctrl'] },
        action: () => cycleBusiness('next'),
        category: 'actions',
        icon: Store,
        keywords: ['business', 'switch', 'store', 'venue'],
      })
    );

    unregisters.push(
      registerCommand({
        id: 'switch-business-prev',
        label: 'Previous Business',
        description: 'Switch to previous business',
        keys: { key: '[', modifiers: ['ctrl'] },
        action: () => cycleBusiness('prev'),
        category: 'actions',
        icon: Store,
        keywords: ['business', 'switch', 'store', 'venue'],
      })
    );

    return () => unregisters.forEach((fn) => fn());
  }, [
    registerCommand,
    navigate,
    toggleSidebar,
    toggleAI,
    cycleBusiness,
    setBusinessDashboardTab,
    setEmployeeDashboardTab,
    openAccountSettings,
    handleOpenBusinessSettings,
    navigateDashboard,
    navigateTab,
  ]);

  return null;
}
